/**
 * Atlas Service
 * 
 * The "Librarian" of the application. Responsible for managing the manifest
 * of Capability Guides and providing context-aware help.
 */

import {
    getDays,
    getPlaces,
    getTravel,
    getAccommodation,
    getDocs
} from '@trip-planner-monorepo/shared';

let manifest = null;
const listeners = new Set();

export const atlasService = {
    /**
     * Initialize the service and load the manifest
     */
    async init() {
        try {
            const response = await fetch('/docs/manifest.json');
            if (!response.ok) {
                console.warn('Help manifest not found, using fallback registry');
                manifest = this.getFallbackManifest();
            } else {
                manifest = await response.json();
            }
            console.log('Atlas Service initialized with manifest:', manifest);
        } catch (error) {
            console.error('Failed to initialize Atlas Service:', error);
            manifest = this.getFallbackManifest();
        }
    },

    /**
     * Get all registered guides
     */
    getGuides() {
        return manifest ? manifest.guides : [];
    },

    /**
     * Find a guide by its context keyword or ID
     */
    findGuide(query) {
        if (!manifest) return null;
        return manifest.guides.find(g =>
            g.id === query ||
            g.context === query ||
            (g.keywords && g.keywords.includes(query))
        );
    },

    /**
     * Perform a simple keyword search across guide titles and descriptions
     */
    search(query) {
        if (!manifest || !query) return [];
        const lowerQuery = query.toLowerCase();

        return manifest.guides.filter(guide =>
            guide.title.toLowerCase().includes(lowerQuery) ||
            guide.description.toLowerCase().includes(lowerQuery) ||
            (guide.keywords && guide.keywords.some(k => k.toLowerCase().includes(lowerQuery)))
        );
    },

    /**
     * Generates a flattened snapshot of the trip data for AI context
     */
    async getTripSnapshot(userId, tripId) {
        if (!userId || !tripId) return { error: 'Missing userId or tripId' };

        try {
            console.log(`[Atlas] Generating snapshot for trip: ${tripId}`);

            const [days, places, travel, accommodation, documents] = await Promise.all([
                getDays(userId, tripId).catch(() => []),
                getPlaces(userId, tripId).catch(() => []),
                getTravel(userId, tripId).catch(() => []),
                getAccommodation(userId, tripId).catch(() => []),
                getDocs ? getDocs(userId, tripId).catch(() => []) : Promise.resolve([]) // Fallback if getDocs not exported from shared
            ]);

            return {
                tripId,
                timestamp: new Date().toISOString(),
                summary: {
                    dayCount: days.length,
                    placeCount: places.length,
                    travelCount: travel.length,
                    accommodationCount: accommodation.length,
                    documentCount: documents.length
                },
                schedule: days.map(d => ({
                    date: d.date,
                    location: d.location,
                    notes: d.notes?.substring(0, 200) || ''
                })).sort((a, b) => new Date(a.date) - new Date(b.date)),
                bookings: {
                    stays: accommodation.map(a => ({
                        name: a.name,
                        type: a.type,
                        checkIn: a.checkInDate,
                        checkOut: a.checkOutDate
                    })),
                    transport: travel.map(t => ({
                        type: t.type,
                        from: t.from,
                        to: t.to,
                        departure: t.departureTime
                    }))
                },
                highlights: places.map(p => ({
                    name: p.name,
                    address: p.address,
                    rating: p.rating
                })),
                documents: documents.map(doc => ({
                    title: doc.title || doc.name,
                    category: doc.primaryCategory,
                    summary: doc.summary || '',
                    extractedFields: doc.extractedData?.data || {}
                }))
            };
        } catch (error) {
            console.error('[Atlas] Failed to build trip snapshot:', error);
            return { error: 'Failed to build trip snapshot', details: error.message };
        }
    },

    /**
     * Fallback manifest for development/initial state
     */
    getFallbackManifest() {
        return {
            guides: [
                {
                    id: 'CAP-000-Getting-Started',
                    title: 'Getting Started',
                    context: 'general',
                    description: 'A brief introduction to the itineri application.',
                    keywords: ['start', 'introduction', 'welcome']
                },
                {
                    id: 'CAP-001-AI-Document-Scanning',
                    title: 'AI Document Scanning',
                    context: 'documents',
                    description: 'Learn how to use AI to scan your trip documents.',
                    keywords: ['scan', 'pdf', 'ocr', 'ai']
                },
                {
                    id: 'CAP-002-Create-From-Document',
                    title: 'Create From Document',
                    context: 'accommodation',
                    description: 'Instantly create entities from scanned documents.',
                    keywords: ['create', 'auto-fill', 'form']
                },
                {
                    id: 'CAP-003-Managing-Daily-Itinerary',
                    title: 'Daily Itinerary',
                    context: 'days',
                    description: 'Manage your day-by-day plans.',
                    keywords: ['itinerary', 'days', 'schedule']
                },
                {
                    id: 'CAP-008-Travel-Management',
                    title: 'Travel & Transport',
                    context: 'travel',
                    description: 'Manage flights, trains, cars, and other movement.',
                    keywords: ['travel', 'flight', 'transport', 'car']
                },
                {
                    id: 'CAP-006-System-Health-Maintenance',
                    title: 'System Health',
                    context: 'health',
                    description: 'Maintain the integrity of your trip data.',
                    keywords: ['health', 'check', 'errors', 'orphans']
                },
                {
                    id: 'CAP-007-Exporting-Porting-Data',
                    title: 'Data Portability',
                    context: 'export',
                    description: 'Export and import your trip data.',
                    keywords: ['export', 'import', 'backup', 'zip']
                }
            ]
        };
    }
};
